import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseReason, DialogService, SuiDialogRef } from '@sui/dialog';
import { selectUser } from 'src/app/core/state';
import { User } from 'src/app/core/models/core.models';
import { filterMenuByAccessLevel } from 'src/app/core/models/main-menu.models';
import { MenuConfigService } from '../../core/services/menu-config.service';
import { UserAccessLevel } from '../../core/services/user.service';
import {
  CommandPaletteComponent,
  CommandPaletteData,
} from 'src/app/shell/components/command-palette/command-palette.component';
import {
  flattenMenuArea,
  PaletteEntry,
} from 'src/app/shell/components/command-palette/command-palette.utils';

/** The menu areas the palette indexes, in display order, with their labels. */
const PALETTE_AREAS: ReadonlyArray<{ section: string; titleKey: string }> = [
  { section: 'analytics', titleKey: 'command-palette.area.analytics' },
  { section: 'admin', titleKey: 'command-palette.area.admin' },
  { section: 'source-catalog', titleKey: 'command-palette.area.source-catalog' },
  { section: 'sysadmin', titleKey: 'command-palette.area.sysadmin' },
  { section: 'mobile-admin', titleKey: 'command-palette.area.mobile-app' },
];

/** Lowest access level that gets the palette (WAP-1692: Global Admin and up). */
export const COMMAND_PALETTE_MIN_ACCESS_LEVEL = UserAccessLevel.GLOBAL_ADMIN;

/** Recently opened pages kept per user in localStorage (newest first). */
const RECENT_LIMIT = 8;
const RECENT_STORAGE_PREFIX = 'sensika.command-palette.recent.';

interface StoredRecent {
  link: string;
  time: number;
}

/**
 * Opens the command palette over the static routes of every area (WAP-1692).
 * The entries are the main menu config filtered with the same rule the menu
 * itself applies (access level + feature components), flattened to
 * `Area -> Section -> Item` and cached per user / level / features / language,
 * so the index is built once per session, not per keystroke or per open. The
 * palette closes with the chosen entry; this service navigates and records the
 * page in the per-user recent list (localStorage today, backend later).
 */
@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly menuConfig = inject(MenuConfigService);
  private readonly translate = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly user = this.store.selectSignal(selectUser);
  private openRef: SuiDialogRef<unknown, PaletteEntry> | null = null;
  private entryCache: { key: string; entries: PaletteEntry[] } | null = null;

  /** True when the current user may use the palette at all. */
  canOpen(): boolean {
    const level = this.user()?.access_level_id ?? UserAccessLevel.CLIENT_USER;
    return level >= COMMAND_PALETTE_MIN_ACCESS_LEVEL;
  }

  /** Opens the palette; returns true when the request was handled. */
  open(): boolean {
    if (!this.canOpen()) return false;
    if (this.openRef) return true;
    const entries = this.entries();
    const ref = this.dialogService.openSimpleDialog<
      CommandPaletteComponent,
      PaletteEntry
    >(CommandPaletteComponent, {
      data: {
        entries,
        recent: this.readRecent(entries),
      } satisfies CommandPaletteData,
      width: 640,
      height: 560,
      closeOnClick: true,
      closeOnEscape: true,
      blurOverlay: true,
    });
    this.openRef = ref;
    ref.closed.subscribe((event) => {
      if (this.openRef === ref) this.openRef = null;
      const entry = event?.data;
      if (event?.reason === DialogCloseReason.Confirm && entry) {
        this.recordRecent(entry);
        const commands = Array.isArray(entry.link) ? entry.link : [entry.link];
        void this.router.navigate(commands);
      }
    });
    return true;
  }

  private entries(): PaletteEntry[] {
    const user = this.user();
    const key = [
      user?.id,
      user?.access_level_id,
      JSON.stringify(user?.feature_components ?? {}),
      this.translate.currentLang,
    ].join('|');
    if (this.entryCache?.key !== key) {
      this.entryCache = { key, entries: this.buildEntries(user) };
    }
    return this.entryCache.entries;
  }

  private buildEntries(user: User | null | undefined): PaletteEntry[] {
    const level = user?.access_level_id ?? UserAccessLevel.CLIENT_USER;
    return PALETTE_AREAS.flatMap(({ section, titleKey }) =>
      flattenMenuArea(
        this.translate.instant(titleKey),
        filterMenuByAccessLevel(
          this.menuConfig.getDefaultMenuItems(section),
          level,
          user?.feature_components,
        ),
      ),
    );
  }

  private linkKey(link: PaletteEntry['link']): string {
    return Array.isArray(link) ? link.join('/') : link;
  }

  private storageKey(): string {
    return RECENT_STORAGE_PREFIX + (this.user()?.id ?? 'anonymous');
  }

  private readStored(): StoredRecent[] {
    try {
      const raw = localStorage.getItem(this.storageKey());
      return raw ? (JSON.parse(raw) as StoredRecent[]) : [];
    } catch {
      return [];
    }
  }

  /** Stored links resolved against the current entries; pages no longer offered drop out. */
  private readRecent(entries: PaletteEntry[]): PaletteEntry[] {
    const byLink = new Map(entries.map((e) => [this.linkKey(e.link), e]));
    return this.readStored()
      .map((r) => byLink.get(r.link))
      .filter((e): e is PaletteEntry => !!e)
      .slice(0, RECENT_LIMIT);
  }

  private recordRecent(entry: PaletteEntry): void {
    try {
      const link = this.linkKey(entry.link);
      const next: StoredRecent[] = [
        { link, time: Date.now() },
        ...this.readStored().filter((r) => r.link !== link),
      ].slice(0, RECENT_LIMIT);
      localStorage.setItem(this.storageKey(), JSON.stringify(next));
    } catch {
      // Storage unavailable (private window, quota): recents are a convenience.
    }
  }
}
